import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { APIClient } from "../../helpers/api_helper";
import * as URL from "../../helpers/url_helper";

const api = new APIClient();

// Wisp AI — draft reply, summarise lead, classify intent.
export const draftReply = createAsyncThunk("ai/draftReply",
  async (conversationId) => api.create(`${URL.AI_DRAFT_REPLY}/${conversationId}/draft`, {}));

// Phone-keyed variant — used by the WhatsApp Inbox UI which is keyed on phone.
export const draftReplyByPhone = createAsyncThunk("ai/draftReplyByPhone",
  async (phone) => {
    const data = await api.create(URL.AI_WHATSAPP_DRAFT, { phone });
    return { phone, ...data };
  });

export const explainLeadScore = createAsyncThunk("ai/explainScore",
  async (leadId) => {
    const data = await api.get(`${URL.AI_LEAD_SCORE_EXPLAIN}/${leadId}/score-explanation`);
    return { leadId, ...data };
  });

export const fetchLeadSummary = createAsyncThunk("ai/summary",
  async ({ leadId, force = false }) => {
    const data = await api.get(`${URL.AI_LEAD_SUMMARY}/${leadId}/summary${force ? "?force=true" : ""}`);
    return { leadId, ...data };
  });

export const scoreLeadIntent = createAsyncThunk("ai/score",
  async (leadId) => {
    const data = await api.create(`${URL.AI_LEAD_SCORE}/${leadId}/score`, {});
    return { leadId, ...data };
  });

export const fetchAiQuota = createAsyncThunk("ai/quota",
  async () => api.get(URL.AI_QUOTA));

export const setAiConsent = createAsyncThunk("ai/consent",
  async (consent) => api.create(URL.AI_CONSENT, { consent }));

const slice = createSlice({
  name: "ai",
  initialState: {
    drafting: false,
    summarizing: false,
    scoring: false,
    error: null,
    tokensRemaining: null,
    consent: false,
    summariesByLead: {},   // leadId -> summary text
    intentsByLead: {},     // leadId -> "BUYING" | "INFO" | "SPAM" | "UNKNOWN"
    lastDraft: null,       // { conversationId, draft }
    draftsByPhone: {},     // phone -> { draft, ts }  for WhatsApp inbox suggest-reply
    explanationsByLead: {},// leadId -> { score, heat, factors, summary }
  },
  reducers: {
    clearLastDraft: (s) => { s.lastDraft = null; },
    clearDraftForPhone: (s, a) => { delete s.draftsByPhone[a.payload]; },
  },
  extraReducers: (b) => {
    b.addCase(draftReply.pending,   (s) => { s.drafting = true; s.error = null; })
     .addCase(draftReply.fulfilled, (s, a) => {
       s.drafting = false;
       s.lastDraft = { draft: a.payload.draft };
       if (a.payload.tokensRemaining != null) s.tokensRemaining = a.payload.tokensRemaining;
     })
     .addCase(draftReply.rejected,  (s, a) => { s.drafting = false; s.error = a.error.message; })

     .addCase(draftReplyByPhone.pending,   (s) => { s.drafting = true; s.error = null; })
     .addCase(draftReplyByPhone.fulfilled, (s, a) => {
       s.drafting = false;
       s.draftsByPhone[a.payload.phone] = { draft: a.payload.draft, ts: Date.now() };
       if (a.payload.tokensRemaining != null) s.tokensRemaining = a.payload.tokensRemaining;
     })
     .addCase(draftReplyByPhone.rejected,  (s, a) => { s.drafting = false; s.error = a.error.message; })

     .addCase(explainLeadScore.fulfilled, (s, a) => {
       s.explanationsByLead[a.payload.leadId] = a.payload;
     })

     .addCase(fetchLeadSummary.pending,   (s) => { s.summarizing = true; s.error = null; })
     .addCase(fetchLeadSummary.fulfilled, (s, a) => {
       s.summarizing = false;
       s.summariesByLead[a.payload.leadId] = a.payload.summary;
       if (a.payload.tokensRemaining != null) s.tokensRemaining = a.payload.tokensRemaining;
     })
     .addCase(fetchLeadSummary.rejected,  (s, a) => { s.summarizing = false; s.error = a.error.message; })

     .addCase(scoreLeadIntent.pending,   (s) => { s.scoring = true; s.error = null; })
     .addCase(scoreLeadIntent.fulfilled, (s, a) => {
       s.scoring = false;
       s.intentsByLead[a.payload.leadId] = a.payload.intent;
       if (a.payload.tokensRemaining != null) s.tokensRemaining = a.payload.tokensRemaining;
     })
     .addCase(scoreLeadIntent.rejected,  (s, a) => { s.scoring = false; s.error = a.error.message; })

     .addCase(fetchAiQuota.fulfilled, (s, a) => {
       s.tokensRemaining = a.payload.tokensRemaining;
       s.consent = !!a.payload.consent;
     })
     .addCase(setAiConsent.fulfilled, (s, a) => {
       s.consent = !!a.payload.consent;
       if (a.payload.tokensRemaining != null) s.tokensRemaining = a.payload.tokensRemaining;
     });
  },
});

export const { clearLastDraft, clearDraftForPhone } = slice.actions;
export default slice.reducer;
